import React, { useState, useEffect } from "react";
import { Form, Container, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { sendPasswordRecoveryEmail } from "../actions/userActions";
import LoaderComponent from "../components/LoaderComponent";
import { StyledInput, ModernButton } from "../components/DefaultImports";

const StyledCard = styled.div`
  width: 100%;
  max-width: 500px;
  padding: 15px;
  margin: 0 auto;
  box-sizing: border-box;

  @media (max-width: 576px) {
    padding: 10px;
  }
`;

const Title = styled.h3`
  text-transform: uppercase;
  color: var(--app-orange, #ee754c);
`;

const MessageRow = styled.div`
  display: flex;
  align-items: flex-start;
  font-size: 16px;
  color: #333;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const IconStyled = styled(FontAwesomeIcon)`
  color: #ee754c;
  font-size: 18px;
  margin-right: 8px;
  margin-top: 4px;
`;

const MessageText = styled.span`
  flex: 1;
  line-height: 1.5;
`;

const BackToLogin = styled.div`
  margin-top: 20px;
  color: #ee754c;
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
  text-align: center;

  &:hover {
    text-decoration: none;
    color: #d65f34;
  }
`;

function AccountRecovery({ onSwitchToLogin }) {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [recoveryEmailSent, setRecoveryEmailSent] = useState(false);
  const [activateLoader, setActivateLoader] = useState(false);
  const [emailWS, setEmailWS] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const dispatch = useDispatch();
  const userUpdate = useSelector((state) => state.userUpdate);
  const { passwordRecovery, loadingPasswordRecovery, passwordRecoveryError } =
    userUpdate;

  const handleSubmit = (event) => {
    event.preventDefault();
    setHasSubmitted(true);

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!emailRegex.test(email)) {
      setEmailWS(true);
    } else {
      setEmailWS(false);
      dispatch(sendPasswordRecoveryEmail(email));
    }
  };

  useEffect(() => {
    setActivateLoader(loadingPasswordRecovery);
  }, [loadingPasswordRecovery]);

  useEffect(() => {
    if (passwordRecovery && passwordRecovery.includes("username_exists")) {
      setRecoveryEmailSent(true);
    }
  }, [passwordRecovery]);

  return (
    <Container
      fluid
      style={{
        padding: 0,
        margin: 0,
        width: "120%",
        maxWidth: "120%",
      }}
    >
      <Row className="justify-content-md-center">
        <Col md={6}>
          <StyledCard>
            {!recoveryEmailSent ? (
              <Form onSubmit={handleSubmit}>
                <Title>{t("accountRecovery.title")}</Title>

                <Form.Group controlId="formBasicEmail">
                  <StyledInput
                    type="email"
                    placeholder={t("auth.email")}
                    value={email}
                    required
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailWS(false);
                      setHasSubmitted(false);
                    }}
                  />

                  <Form.Text className="text-muted">
                    {t("accountRecovery.form.emailHelp")}
                  </Form.Text>

                  {hasSubmitted &&
                    passwordRecoveryError &&
                    passwordRecoveryError.includes("username_does_not_exist") &&
                    !loadingPasswordRecovery && (
                      <Form.Text className="text-danger">
                        <br />
                        {t("accountRecovery.errors.emailNotFound")}
                      </Form.Text>
                    )}

                  {hasSubmitted && emailWS && (
                    <Form.Text className="text-danger">
                      <br />
                      {t("accountRecovery.errors.invalidEmail")}
                    </Form.Text>
                  )}
                </Form.Group>

                <ModernButton
                  type="submit"
                  color="#f59878"
                  fontSize="16px"
                  style={{ width: "100%", marginTop: "20px" }}
                >
                  {t("accountRecovery.actions.recoverPassword")}
                </ModernButton>

                <BackToLogin onClick={onSwitchToLogin}>
                  {t("accountRecovery.actions.backToLogin")}
                </BackToLogin>
              </Form>
            ) : (
              <div>
                <Title>{t("accountRecovery.success.title")}</Title>

                <MessageRow>
                  <IconStyled icon={faCircleCheck} />
                  <MessageText>
                    {t("accountRecovery.success.message")}{" "}
                    <span style={{ fontWeight: "bold", fontFamily: "Arial" }}>
                      {email}
                    </span>
                  </MessageText>
                </MessageRow>

                <BackToLogin onClick={onSwitchToLogin}>
                  {t("accountRecovery.actions.backToLogin")}
                </BackToLogin>
              </div>
            )}
          </StyledCard>
        </Col>
      </Row>

      <LoaderComponent activate={activateLoader} theSize={"o"} />
    </Container>
  );
}

export default AccountRecovery;
